import React, { useCallback, useEffect, useState } from 'react';
import { Checkbox, Input, Modal, Button, message, Space } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { SearchOutlined, FilterOutlined, CloseOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import CommonTable from 'components/CommonTable';
import { PATH } from 'constants/path';
import { getHelloKlarityHost } from 'utils/common';
import { getGPPracticesList, updateGPListingStatus, IGPPracticeItem } from 'api/operation/group';
import { EGPListingStatus } from 'types/operation';
import s from '../s.module.less';

const STATUS_OPTIONS = [
    { value: EGPListingStatus.INIT, label: 'Not activated' },
    { value: EGPListingStatus.LISTED, label: 'Activated' },
    { value: EGPListingStatus.PAUSE_LISTED, label: 'Paused' },
    { value: EGPListingStatus.UNLISTED, label: 'Unlisted' },
];

const STATE_OPTIONS = [
    { value: 'CA', label: 'CA' },
    { value: 'WA', label: 'WA' },
    { value: 'FL', label: 'FL' },
    { value: 'TX', label: 'TX' },
    { value: 'NY', label: 'NY' },
    { value: 'NV', label: 'NV' },
    { value: 'OR', label: 'OR' },
    { value: 'ID', label: 'ID' },
    { value: 'AZ', label: 'AZ' },
    { value: 'IL', label: 'IL' },
    { value: 'IN', label: 'IN' },
];

const PracticesTable = () => {
    const [pendingNameFilter, setPendingNameFilter] = useState('');
    const [nameFilter, setNameFilter] = useState('');
    const [pendingOwnerFilter, setPendingOwnerFilter] = useState('');
    const [ownerFilter, setOwnerFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState<string[]>([]);
    const [stateFilter, setStateFilter] = useState<string[]>([]);
    const [current, setCurrent] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<IGPPracticeItem[]>([]);
    const [total, setTotal] = useState(0);
    const [deleteModal, setDeleteModal] = useState<IGPPracticeItem | null>(null);
    const [relistModal, setRelistModal] = useState<IGPPracticeItem | null>(null);
    const [actionLoading, setActionLoading] = useState(false);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getGPPracticesList({
                current,
                size: pageSize,
                name: nameFilter || undefined,
                adminKeyword: ownerFilter || undefined,
                listingStatus: statusFilter.length > 0 ? statusFilter : undefined,
                states: stateFilter.length > 0 ? stateFilter : undefined,
            });
            if (res && !res.error) {
                setData(res.data?.data?.records || []);
                setTotal(res.data?.data?.total || 0);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [current, pageSize, nameFilter, ownerFilter, statusFilter, stateFilter]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleDeleteConfirm = useCallback(async () => {
        if (!deleteModal) return;
        setActionLoading(true);
        try {
            const res = await updateGPListingStatus(deleteModal.id, { targetStatus: 'UNLISTED' });
            if (res && !res.error) {
                message.success(`"${deleteModal.name}" has been successfully unlisted.`);
                setDeleteModal(null);
                fetchData();
            } else {
                message.error(res?.error || 'Failed to delete listing');
            }
        } catch (e) {
            console.error(e);
        } finally {
            setActionLoading(false);
        }
    }, [deleteModal, fetchData]);

    const handleRelistConfirm = useCallback(async () => {
        if (!relistModal) return;
        setActionLoading(true);
        try {
            const res = await updateGPListingStatus(relistModal.id, { targetStatus: 'LISTED' });
            if (res && !res.error) {
                message.success('Practice re-listed');
                setRelistModal(null);
                fetchData();
            } else {
                message.error(res?.error || 'Failed to re-list');
            }
        } catch (e) {
            console.error(e);
        } finally {
            setActionLoading(false);
        }
    }, [relistModal, fetchData]);

    const columns: ColumnsType<IGPPracticeItem> = [
        {
            title: 'PRACTICE',
            dataIndex: 'name',
            width: '260px',
            fixed: 'left',
            className: s.tableColumn,
            filterIcon: <SearchOutlined />,
            filterDropdown: ({ close }) => (
                <div style={{ padding: 16 }} onKeyDown={(e) => e.stopPropagation()}>
                    <Input
                        placeholder="Search name"
                        value={pendingNameFilter}
                        onChange={(e) => setPendingNameFilter(e.target.value)}
                        onPressEnter={() => {
                            setNameFilter(pendingNameFilter);
                            setCurrent(1);
                            close();
                        }}
                        style={{ marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => {
                                setNameFilter(pendingNameFilter);
                                setCurrent(1);
                                close();
                            }}
                            icon={<SearchOutlined />}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Search
                        </Button>
                        <Button
                            onClick={() => {
                                setPendingNameFilter('');
                                setNameFilter('');
                                setCurrent(1);
                                close();
                            }}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Reset
                        </Button>
                    </Space>
                </div>
            ),
        },
        {
            title: 'OWNER',
            dataIndex: 'admin',
            width: '260px',
            fixed: 'left',
            className: s.tableColumn,
            filterIcon: <SearchOutlined />,
            filterDropdown: ({ close }) => (
                <div style={{ padding: 16 }} onKeyDown={(e) => e.stopPropagation()}>
                    <Input
                        placeholder="Search owner"
                        value={pendingOwnerFilter}
                        onChange={(e) => setPendingOwnerFilter(e.target.value)}
                        onPressEnter={() => {
                            setOwnerFilter(pendingOwnerFilter);
                            setCurrent(1);
                            close();
                        }}
                        style={{ marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => {
                                setOwnerFilter(pendingOwnerFilter);
                                setCurrent(1);
                                close();
                            }}
                            icon={<SearchOutlined />}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Search
                        </Button>
                        <Button
                            onClick={() => {
                                setPendingOwnerFilter('');
                                setOwnerFilter('');
                                setCurrent(1);
                                close();
                            }}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Reset
                        </Button>
                    </Space>
                </div>
            ),
            render: (_: string, row: IGPPracticeItem) => (
                <div>
                    <div className={s.practiceNamePrimary}>{row.admin}</div>
                    <div className={s.practiceNameSub}>{row.adminEmail}</div>
                </div>
            ),
        },
        {
            title: 'GPP STATUS',
            dataIndex: 'listingStatus',
            width: '160px',
            className: s.tableColumn,
            filterIcon: <FilterOutlined style={{ color: statusFilter.length > 0 ? '#F6891F' : undefined }} />,
            filterDropdown: ({ confirm }) => (
                <div className={s.filterDropdown} onKeyDown={(e) => e.stopPropagation()}>
                    <Checkbox.Group
                        value={statusFilter}
                        onChange={(vals) => {
                            setStatusFilter(vals as string[]);
                            setCurrent(1);
                            confirm({ closeDropdown: true });
                        }}
                    >
                        <div className={s.filterCheckbox} style={{ padding: '4px' }}>
                            {STATUS_OPTIONS.map((opt) => (
                                <Checkbox key={opt.value} value={opt.value}>
                                    {opt.label}
                                </Checkbox>
                            ))}
                        </div>
                    </Checkbox.Group>
                </div>
            ),
            render: (status: EGPListingStatus | null) => {
                if (!status) return '-';
                const map: Record<EGPListingStatus, { cls: string; label: string }> = {
                    [EGPListingStatus.LISTED]: { cls: s.listed, label: 'Activated' },
                    [EGPListingStatus.PAUSE_LISTED]: { cls: s.paused, label: 'Paused' },
                    [EGPListingStatus.UNLISTED]: { cls: s.inactive, label: 'Unlisted' },
                    [EGPListingStatus.INIT]: { cls: s.pending, label: 'Not activated' },
                };
                const item = map[status];
                if (!item) return status;
                return <span className={`${s.statusBadge} ${item.cls}`}>{item.label}</span>;
            },
        },
        {
            title: 'PROVIDERS',
            dataIndex: 'providers',
            width: '120px',
            className: s.tableColumn,
        },
        {
            title: 'STATE',
            dataIndex: 'state',
            width: '140px',
            className: s.tableColumn,
            filterIcon: <FilterOutlined style={{ color: stateFilter.length > 0 ? '#F6891F' : undefined }} />,
            filterDropdown: ({ confirm }) => (
                <div className={s.filterDropdown} onKeyDown={(e) => e.stopPropagation()}>
                    <Checkbox.Group
                        value={stateFilter}
                        onChange={(vals) => {
                            setStateFilter(vals as string[]);
                            setCurrent(1);
                            confirm({ closeDropdown: true });
                        }}
                    >
                        <div className={s.filterCheckbox}>
                            {STATE_OPTIONS.map((opt) => (
                                <Checkbox key={opt.value} value={opt.value}>
                                    {opt.label}
                                </Checkbox>
                            ))}
                        </div>
                    </Checkbox.Group>
                </div>
            ),
            render: (state: string) => state || '-',
        },
        {
            title: 'PROFILE LINK',
            dataIndex: 'slug',
            width: '180px',
            className: s.tableColumn,
            render: (slug: string) =>
                slug ? (
                    <a
                        className={s.profileLink}
                        href={`${getHelloKlarityHost()}/group/${slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Link
                    </a>
                ) : (
                    <span>-</span>
                ),
        },
        {
            title: 'KLARITY LAUNCH DATE',
            dataIndex: 'klarityLaunchDate',
            width: '200px',
            className: s.tableColumn,
            render: (date: string | null) => (date ? dayjs(date).format('MM/DD/YYYY') : '-'),
        },
        {
            title: 'ACTION',
            width: '280px',
            fixed: 'right',
            className: s.tableColumn,
            render: (_: unknown, row: IGPPracticeItem) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                    <span style={{ minWidth: 180 }}>
                        {row.listingStatus === EGPListingStatus.LISTED && (
                            <span className={s.actionDanger} onClick={() => setDeleteModal(row)}>
                                Delete Listing (Danger)
                            </span>
                        )}
                        {(row.listingStatus === EGPListingStatus.PAUSE_LISTED ||
                            row.listingStatus === EGPListingStatus.UNLISTED) && (
                            <span className={s.actionRelist} onClick={() => setRelistModal(row)}>
                                Re-list
                            </span>
                        )}
                    </span>
                    <span
                        className={s.actionView}
                        onClick={() => {
                            window.open(`${PATH.OPERATION}/${PATH.GROUP_PRACTICE_DETAIL}/${row.id}`, '_blank');
                        }}
                    >
                        View
                    </span>
                </div>
            ),
        },
    ];

    return (
        <>
            <CommonTable<IGPPracticeItem>
                rowKey="id"
                bordered
                loading={loading}
                columns={columns}
                data={data}
                pagination={{
                    current,
                    pageSize,
                    total,
                }}
                onChange={(pagination) => {
                    setCurrent(pagination?.current || 1);
                    setPageSize(pagination?.pageSize || 10);
                }}
                scroll={{ x: '1400' }}
            />

            <Modal
                open={!!deleteModal}
                onCancel={() => setDeleteModal(null)}
                title={`Delete listing ${deleteModal?.name}?`}
                closeIcon={<CloseOutlined />}
                footer={
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                        <Button onClick={() => setDeleteModal(null)}>Cancel</Button>
                        <Button type="primary" danger loading={actionLoading} onClick={handleDeleteConfirm}>
                            Delete
                        </Button>
                    </div>
                }
                destroyOnClose
            >
                <p style={{ fontSize: 14, color: '#374151' }}>
                    This will permanently remove the listing for {deleteModal?.name} from Klarity. Patients will no
                    longer be able to find this practice on the platform. This action cannot be undone.
                </p>
            </Modal>

            <Modal
                open={!!relistModal}
                onCancel={() => setRelistModal(null)}
                title={`Re-list ${relistModal?.name}?`}
                closeIcon={<CloseOutlined />}
                footer={
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                        <Button onClick={() => setRelistModal(null)}>Cancel</Button>
                        <Button
                            type="primary"
                            loading={actionLoading}
                            onClick={handleRelistConfirm}
                            style={{ backgroundColor: '#16A34A', borderColor: '#16A34A' }}
                        >
                            Re-list
                        </Button>
                    </div>
                }
                destroyOnClose
            >
                <p style={{ fontSize: 14, color: '#4B5563' }}>
                    This will restore the practice listing on Hello Klarity. All {relistModal?.providers || 0} providers
                    will need to be re-listed individually if you want their profiles searchable too.
                </p>
            </Modal>
        </>
    );
};

export default PracticesTable;
